import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Snackbar, Stack, TextField } from "@mui/material";
import { taskActions } from "@common-reducers/TasksReducer";
import { useRouter } from "@common-reducers/hooks/use-router";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { useCallback, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { BiBriefcase } from "react-icons/bi";
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { getCurrentWorkspaceUserList, userRoleCountSelector } from "@common-reducers/BoardsSelectors";
import BTButton from "@common-components/bt-button/bt-button";
import BTInputField from "@common-components/bt-input-field/bt-input-field";
import { UserManagementFilter } from "./user-management-filter";
import { MdAdd } from "react-icons/md";
import { workspaceActions } from "@common-reducers/WorkspaceReducer";
import { selectCurrentWorkspace } from "@common-reducers/UserSelectors";
import { DBWorkspaceUserThunks } from "@common-reducers/DBServiceThunks";
import { UserManagementExport } from "./user-management-export";
import { namespace } from "@common-config/constants";
import { useTranslation } from "react-i18next";

type WorkspaceUser = { _id?: string; email?: string; firstName?: string; lastName?: string; role?: string; status?: string; userId?: string };

export const UserManagementHeader = ({ readOnly }: { readOnly?: boolean }) => {
    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const router = useRouter();
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const numOfGuests = useAppSelector((state) => userRoleCountSelector(state, 'guest')) as number;
    const numOfMembers = useAppSelector((state) => userRoleCountSelector(state, 'member')) as number;
    // The owner takes a seat too (seats = owner + members), so the seat counter includes them.
    const numOfOwners = useAppSelector((state) => userRoleCountSelector(state, 'owner')) as number;
    const currentWorkspace = useAppSelector(selectCurrentWorkspace);
    const workspaceUserList = useAppSelector(getCurrentWorkspaceUserList) as WorkspaceUser[];

    const snackbarMessage = useAppSelector((state) => state.TasksReducer.taskSnackbarMessage);
    const handleClose = useCallback(() => {
        dispatch(taskActions.setTaskSnackbarMessage(''));
    }, []);


    const onSearchChange = (query: string) => {
        dispatch(workspaceActions.setWorkSpaceUsersFilter(query))
    }

    function filterWorkspaceUser(query: string) {
        setEmail(query)
    }

    const handleCloseInviteDialog = useCallback(() => {
        setInviteModalOpen(false);
    }, []);

    const inviteUser = useCallback(async () => {
        setIsLoading(true);
        const res = await dispatch(
            DBWorkspaceUserThunks.create({
                email,
                role: 'member',
                status: 'pending',
                workspaceId: currentWorkspace?._id
            })
        ).unwrap();
        if ((res as any).status === 500) {
            const error = (res as any).data;
            dispatch(taskActions.setTaskSnackbarMessage(error.message));
        }

        setEmail('');
        setIsLoading(false);
        setInviteModalOpen(false);

    }, [currentWorkspace?._id, dispatch, email]);

    const emailExists = useMemo(
        () =>
            workspaceUserList.find((user) => user?.email === email),
        [workspaceUserList, email]
    );



    const isValidEmail = useMemo(() => {
        const regex = /^((([!#$%&'*+\-/=?^_`{|}~\w])|([!#$%&'*+\-/=?^_`{|}~\w][!#$%&'*+\-/=?^_`{|}~\.\w]{0,}[!#$%&'*+\-/=?^_`{|}~\w]))[@]\w+([-.]\w+)*\.\w+([-.]\w+)*)$/;
        return email.match(regex);
    }, [email]);

    const canInvite = useMemo(
        () => isValidEmail && !isLoading && !emailExists,
        [emailExists, isLoading, isValidEmail]
    );
    const dialogDirection = i18n.dir(i18n.language) === 'rtl' ? 'rtl' : 'ltr';

    return (
        <>
            <div style={{ display: 'flex', gap: '32px' }}>
                <div style={{
                    display: 'flex',
                    gap: '6px',
                    alignItems: 'center',
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '21px',

                }}>
                    <PeopleAltOutlinedIcon sx={{ height: '15px', marginInlineEnd: "-4px" }} />
                    <div>
                        <span style={{
                            fontWeight: '700'
                        }}>{numOfMembers + numOfOwners} </span>
                        <span> {t('userManagementHeader.members')}</span>
                    </div>
                    <BTButton onClick={() => {
                        router.navigate('/account-subscription', router.query)
                    }} sx={{
                        position: "relative",
                        top: "1.3px"
                    }} btvariant="upgrade">
                        {t('userManagementHeader.upgrade')}
                    </BTButton>

                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '21px',
                }}>
                    <BiBriefcase />
                    <div>
                        <span style={{
                            fontWeight: '700'
                        }}>{numOfGuests} </span>
                        <span> {t('userManagementHeader.guestsFree')}</span>

                    </div>

                </div>
            </div>

            <Stack alignItems={"center"} direction="row" my={2}  >
                <Stack direction="row" spacing={4} alignItems={"center"} >

                    <BTInputField
                        style={{
                            width: "300px",
                        }}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={t('userManagementHeader.searchMembersPlaceholder')}
                        sx={{
                            backgroundColor: 'var(--color-surface)',
                        }}
                    />

                    <UserManagementFilter />
                    <UserManagementExport/>
           
                </Stack>


                <BTButton
                disabled={readOnly}
                    sx={{
                        width: '200px',
                        ml: 'auto',
                        ".MuiButton-startIcon": { marginInlineStart: '-4px', marginInlineEnd: '8px' },
                        color: 'var(--color-always-white) !important',
                    }}
                    onClick={() => setInviteModalOpen(true)}
                    startIcon={<MdAdd />}
                >
                    {t('userManagementHeader.invite')}
                </BTButton>





            </Stack>

            <Dialog
                open={inviteModalOpen}
                onClose={() => setInviteModalOpen(false)}
                PaperProps={{
                    sx: {
                        direction: dialogDirection,
                        textAlign: 'start',
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'start' }}>
                    {t('userManagementHeader.inviteDialogTitle')}{" "}
                    <bdi>
                        <b>{currentWorkspace?.name ?? t('userManagementHeader.workspace')}</b>
                    </bdi>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'start' }}>
                    <DialogContentText sx={{ textAlign: 'start' }}>
                        {t('userManagementHeader.inviteDialogDescription')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('userManagementHeader.emailAddressLabel')}
                        type="email"
                        fullWidth
                        variant="standard"
                        value={email}
                        onChange={({ target }: any) => filterWorkspaceUser(target.value)}
                        inputProps={{
                            dir: 'ltr',
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                textAlign: 'start',
                            },
                            '& .MuiInputLabel-root': {
                                textAlign: 'start',
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ direction: dialogDirection }}>
                    <BTButton onClick={handleCloseInviteDialog} btvariant="text">{t('userManagementHeader.cancelButton')}</BTButton>
                    <BTButton disabled={!canInvite} onClick={inviteUser}>{t('userManagementHeader.inviteButton')}</BTButton>
                </DialogActions>
            </Dialog>
            <Snackbar
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                open={!!snackbarMessage}
                autoHideDuration={8000}
                onClose={handleClose}
                sx={{
                    "& .MuiPaper-root": {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }

                }}
            >
                <Alert severity="info" >
                    <Stack direction="row" alignItems={"center"}>

                        <Box>{snackbarMessage}</Box>
                        {/* The backend message is English ("Seat limit exceeded"): match it in any UI language. */}
                        {(snackbarMessage === t('userManagementHeader.seatLimitExceeded') || /seat limit exceeded/i.test(snackbarMessage ?? '')) && <Button sx={{ mr: "10px", textTransform: "capitalize", color: "inherit" }} variant="text" onClick={() => router.navigate('/account-subscription', router.query)}><span style={{ borderBottom: "1px solid var(--color-text)", fontWeight: "bold" }}>{t('userManagementHeader.snackbarAddMoreSeats')}</span></Button>}


                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={handleClose}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Alert>
            </Snackbar>

        </>
    );
};
